import { useId, useMemo } from 'react';

/**
 * Sankey flow diagram, drawn as plain SVG.
 *
 * Deliberately not a Chart.js plugin. The sankey plugin renders to canvas,
 * which means no selectable text, no CSS control over labels, and no way to let
 * a label sit outside the plot area — all three of which are exactly what makes
 * a cash-flow diagram readable. SVG also scales to a phone without going
 * blurry, and the whole layout is about eighty lines of arithmetic.
 *
 * Layout is a simple layered assignment: nodes are placed in the column you
 * give them, sized in proportion to the larger of their in/out flow, and
 * stacked in the order the links arrive. That's enough for a budget, which is
 * a tree — it would need a proper crossing-minimisation pass for a graph with
 * genuine many-to-many flow.
 */

export interface SankeyNode {
  id: string;
  label: string;
  /** 0-based column. Nodes in the same column are stacked top to bottom. */
  depth: number;
  colour: string;
  /** Optional emoji or short prefix rendered before the label. */
  icon?: string | null;
  /**
   * The number the label should state, when it differs from the node's size.
   *
   * A node is sized by `max(inflow, outflow)` because it has to be tall enough
   * for every ribbon touching it. That is the right rule for the geometry and
   * the wrong one for the caption: a node whose outflows exceed its inflow then
   * *reads* as the larger number, which is how "Take-home" came to display the
   * committed total — and a percentage computed off it — instead of the pay that
   * actually arrived. Set this to keep the caption honest while the box stays
   * big enough to draw.
   */
  displayValue?: number;
}

export interface SankeyLink {
  from: string;
  to: string;
  value: number;
}

interface Placed extends SankeyNode {
  value: number;
  x: number;
  y: number;
  height: number;
  /** Vertical centre of the label block after de-overlapping. */
  labelY: number;
  /** Name and value on one line — for nodes too short for a stacked block. */
  oneLine: boolean;
}

const NODE_W = 9;
const GAP = 10;            // vertical gap between stacked nodes
const PAD_TOP = 6;
const TWO_LINE_BLOCK = 30; // name over value
const ONE_LINE_BLOCK = 16; // name · value inline

/** Fonts used by the labels — needed for width estimation, defined once. */
const FS_NAME = 12.5;
const FS_VALUE = 12;

/**
 * Estimated rendered width of a label line. There's no way to measure SVG text
 * before rendering it, but body-font average glyph width is close enough to
 * 0.56em for layout, and emoji/icons run about 1.2em.
 */
function estWidth(text: string, fs: number, hasIcon: boolean): number {
  return text.length * fs * 0.56 + (hasIcon ? fs * 1.4 : 0);
}

export function SankeyChart({
  nodes, links, height = 420, width = 1000, formatValue,
  compact = false, minScale = 0.8,
}: {
  nodes: SankeyNode[];
  links: SankeyLink[];
  height?: number;
  /** Minimum coordinate-space width; the layout grows past it if labels need room. */
  width?: number;
  formatValue: (v: number) => string;
  /** Drop the share percentages — for narrow screens. */
  compact?: boolean;
  /**
   * Text legibility floor. The SVG scales into its container, and below this
   * fraction of coordinate size the container scrolls instead of the text
   * shrinking further — the labels are the part carrying the numbers.
   */
  minScale?: number;
}) {
  const gradId = useId();

  const model = useMemo(() => {
    const byId = new Map(nodes.map(n => [n.id, n]));
    const inflow = new Map<string, number>();
    const outflow = new Map<string, number>();
    for (const l of links) {
      if (!byId.has(l.from) || !byId.has(l.to)) continue;
      outflow.set(l.from, (outflow.get(l.from) ?? 0) + l.value);
      inflow.set(l.to, (inflow.get(l.to) ?? 0) + l.value);
    }

    const depths = [...new Set(nodes.map(n => n.depth))].sort((a, b) => a - b);
    const maxDepth = depths[depths.length - 1] ?? 0;

    const grandTotal = nodes
      .filter(n => n.depth === 0)
      .reduce((s, n) => s + (outflow.get(n.id) ?? 0), 0);

    /*
     * Column spacing is derived from the labels, not divided evenly.
     *
     * Even division was the overlap bug: a middle column's name and amount
     * render to its right, into the gap before the next column — and the gap
     * had no idea how long "Retirement — 401K $1,200 (15.6%)" is. Estimate
     * the widest label per column and make each gap at least that plus
     * clearance. The diagram simply gets wider than `width` if it must; the
     * container scrolls, which is the trade the layout is built around.
     */
    // Node heights come first: whether a label renders as two lines or one
    // (and therefore how wide it runs) depends on how tall its node is.
    let unit = Infinity;
    for (const d of depths) {
      const col = nodes.filter(n => n.depth === d);
      const total = col.reduce(
        (s, n) => s + Math.max(inflow.get(n.id) ?? 0, outflow.get(n.id) ?? 0), 0,
      );
      if (total <= 0) continue;
      const available = height - PAD_TOP * 2 - GAP * Math.max(0, col.length - 1);
      unit = Math.min(unit, available / total);
    }
    if (!Number.isFinite(unit) || unit <= 0) unit = 0;

    const heightOf = (id: string) =>
      Math.max(2, Math.max(inflow.get(id) ?? 0, outflow.get(id) ?? 0) * unit);
    const captionOf = (n: SankeyNode, size: number) => n.displayValue ?? size;
    const valueLineFor = (v: number) => {
      const share = grandTotal > 0 ? ` (${((v / grandTotal) * 100).toFixed(1)}%)` : '';
      return formatValue(v) + (compact ? '' : share);
    };
    // A node too short for a stacked name-over-value block gets one inline
    // line — half the vertical footprint, which is what lets a column of
    // small categories label itself at all.
    const oneLineFor = (id: string) => heightOf(id) < TWO_LINE_BLOCK;

    const labelW = new Map<number, number>();
    for (const d of depths) {
      let w = 0;
      for (const n of nodes.filter(x => x.depth === d)) {
        const v = captionOf(n, Math.max(inflow.get(n.id) ?? 0, outflow.get(n.id) ?? 0));
        const nameW = estWidth(n.label, FS_NAME, !!n.icon);
        const valueW = estWidth(valueLineFor(v), FS_VALUE, false);
        w = Math.max(w, oneLineFor(n.id) ? nameW + valueW + 10 : Math.max(nameW, valueW));
      }
      labelW.set(d, w);
    }

    const MIN_RIBBON = 110;   // room for the curves to read as flows
    const CLEAR = 20;         // air between a label and the next column

    const xs = new Map<number, number>();
    let x = (labelW.get(0) ?? 0) + 10;   // depth-0 labels sit to the left
    for (const d of depths) {
      xs.set(d, x);
      if (d === maxDepth) break;
      // Middle-column labels live inside the gap after their node.
      const needed = d === 0 ? 0 : (labelW.get(d) ?? 0) + CLEAR;
      x += NODE_W + Math.max(MIN_RIBBON, needed + 8 + MIN_RIBBON * 0.35);
    }
    // If the tight layout comes in under the requested width, stretch the
    // ribbon gaps evenly to fill it; label allowances stay untouched.
    const tight = (xs.get(maxDepth) ?? 0) + NODE_W + (labelW.get(maxDepth) ?? 0) + 18;
    let naturalWidth = tight;
    if (tight < width && maxDepth > 0) {
      const extra = width - tight;
      for (const d of depths) xs.set(d, (xs.get(d) ?? 0) + (extra * d) / maxDepth);
      naturalWidth = width;
    }
    const xFor = (d: number) => xs.get(d) ?? 0;

    /*
     * Order each column by where its parents sit, not by the order the caller
     * pushed nodes in.
     *
     * Push order is a trap: the cash-flow diagram pushes Take-home before
     * Deductions at depth 1, but Deductions' children (tax, pre-tax, post-tax)
     * before Take-home's buckets at depth 2 — so the columns were exactly
     * inverted and every ribbon crossed the diagram diagonally. Asking callers
     * to push in a globally consistent order is a rule nobody can hold in their
     * head; this is the standard barycentre pass instead. Each node sorts by the
     * value-weighted mean position of its inflow sources in the previous column,
     * ties falling back to push order so a column of siblings keeps the caller's
     * intent.
     */
    const columns = new Map<number, SankeyNode[]>();
    depths.forEach((d, di) => {
      const col = nodes.filter(n => n.depth === d);
      if (di === 0) { columns.set(d, col); return; }
      const rank = new Map((columns.get(depths[di - 1]) ?? []).map((n, i) => [n.id, i]));
      const bary = new Map<string, number>();
      col.forEach((n, i) => {
        let weighted = 0, total = 0;
        for (const l of links) {
          if (l.to !== n.id) continue;
          const r = rank.get(l.from);
          if (r === undefined) continue;
          weighted += r * l.value;
          total += l.value;
        }
        // No parent in the previous column (a node fed from further back, or an
        // orphan) keeps its push position rather than collapsing to the top.
        bary.set(n.id, total > 0 ? weighted / total : i);
      });
      const pushIndex = new Map(col.map((n, i) => [n.id, i]));
      columns.set(d, col.slice().sort((a, b) =>
        (bary.get(a.id)! - bary.get(b.id)!) || (pushIndex.get(a.id)! - pushIndex.get(b.id)!)));
    });

    const placed = new Map<string, Placed>();
    for (const d of depths) {
      const col = columns.get(d) ?? [];
      const sized = col.map(n => {
        const size = Math.max(inflow.get(n.id) ?? 0, outflow.get(n.id) ?? 0);
        return { node: n, value: captionOf(n, size), h: heightOf(n.id) };
      });
      const stack = sized.reduce((s, x) => s + x.h, 0) + GAP * Math.max(0, col.length - 1);
      let y = PAD_TOP + Math.max(0, (height - PAD_TOP * 2 - stack) / 2);   // centre short columns
      for (const s of sized) {
        placed.set(s.node.id, {
          ...s.node, value: s.value, x: xFor(d), y, height: s.h,
          oneLine: oneLineFor(s.node.id),
          labelY: y + s.h / 2,
        });
        y += s.h + GAP;
      }
    }

    /*
     * Label de-overlap — the fix for columns of small nodes.
     *
     * Node heights are proportional to dollars, but a label needs a fixed
     * ~15/29px of vertical space regardless of how little money it carries. A
     * column of $300 categories stacks its nodes 22px apart and its labels on
     * top of each other. So labels get their own vertical layout: start at
     * each node's centre, sweep down pushing collisions apart, then shift the
     * whole column back up if it overran the bottom. Labels drift from their
     * nodes by a few pixels; that beats being unreadable.
     */
    for (const d of depths) {
      const col = [...placed.values()].filter(p => p.depth === d).sort((a, b) => a.y - b.y);
      const blockOf = (p: Placed) => (p.oneLine ? ONE_LINE_BLOCK : TWO_LINE_BLOCK);
      for (let i = 1; i < col.length; i++) {
        const need = blockOf(col[i - 1]) / 2 + blockOf(col[i]) / 2 + 2;
        if (col[i].labelY - col[i - 1].labelY < need) {
          col[i].labelY = col[i - 1].labelY + need;
        }
      }
      const last = col[col.length - 1];
      if (last) {
        const overflow = last.labelY + blockOf(last) / 2 - height;
        if (overflow > 0) {
          // Push everything up, then re-separate from the top down.
          for (const p of col) p.labelY -= overflow;
          for (let i = 1; i < col.length; i++) {
            const need = blockOf(col[i - 1]) / 2 + blockOf(col[i]) / 2 + 2;
            if (col[i].labelY - col[i - 1].labelY < need) {
              col[i].labelY = col[i - 1].labelY + need;
            }
          }
        }
      }
    }

    // Ribbons, stacked at both ends in link order.
    const outCursor = new Map<string, number>();
    const inCursor = new Map<string, number>();
    const ribbons = links.flatMap(l => {
      const a = placed.get(l.from);
      const b = placed.get(l.to);
      if (!a || !b || l.value <= 0) return [];
      const h = Math.max(1, l.value * unit);
      const y0 = a.y + (outCursor.get(l.from) ?? 0);
      const y1 = b.y + (inCursor.get(l.to) ?? 0);
      outCursor.set(l.from, (outCursor.get(l.from) ?? 0) + h);
      inCursor.set(l.to, (inCursor.get(l.to) ?? 0) + h);

      const x0 = a.x + NODE_W;
      const x1 = b.x;
      const mx = (x0 + x1) / 2;
      return [{
        key: `${l.from}->${l.to}`,
        from: a, to: b, value: l.value,
        d: `M${x0},${y0} C${mx},${y0} ${mx},${y1} ${x1},${y1}`
         + ` L${x1},${y1 + h} C${mx},${y1 + h} ${mx},${y0 + h} ${x0},${y0 + h} Z`,
      }];
    });

    return { placed: [...placed.values()], ribbons, maxDepth, grand: grandTotal, naturalWidth };
  }, [nodes, links, height, width, compact, formatValue]);

  if (!model.placed.length) return null;

  const fs = FS_NAME;
  const fsValue = FS_VALUE;

  return (
    <svg
      viewBox={`0 0 ${model.naturalWidth} ${height}`}
      width="100%"
      // The floor keeps text at ≥ minScale of its coordinate size; below that
      // the container scrolls rather than the labels shrinking into decor.
      style={{ display: 'block', overflow: 'visible', minWidth: Math.round(model.naturalWidth * minScale) }}
      role="img"
      aria-label="Cash flow diagram"
    >
      <defs>
        {model.ribbons.map(r => (
          <linearGradient key={r.key} id={`${gradId}-${r.key}`} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor={r.from.colour} stopOpacity={0.28} />
            <stop offset="100%" stopColor={r.to.colour} stopOpacity={0.42} />
          </linearGradient>
        ))}
      </defs>

      {model.ribbons.map(r => (
        <path key={r.key} d={r.d} fill={`url(#${gradId}-${r.key})`}>
          <title>{`${r.from.label} → ${r.to.label}: ${formatValue(r.value)}`}</title>
        </path>
      ))}

      {model.placed.map(n => {
        const first = n.depth === 0;
        // Ends label outwards, middle columns label inwards from their right
        // edge — the same trick Monarch uses to keep the diagram from needing
        // a legend.
        const anchor = first ? 'end' : 'start';
        const tx = first ? n.x - 8 : n.x + NODE_W + 8;
        const share = model.grand > 0 ? (n.value / model.grand) * 100 : 0;
        const valueText = `${formatValue(n.value)}${!compact && share > 0 ? ` (${share.toFixed(1)}%)` : ''}`;

        return (
          <g key={n.id}>
            <rect x={n.x} y={n.y} width={NODE_W} height={n.height} rx={2} fill={n.colour} />
            {n.oneLine ? (
              // Short node: one inline line at the de-overlapped position.
              <text
                x={tx} y={n.labelY + 4} textAnchor={anchor}
                style={{ fontFamily: 'var(--vn-font-body)', fontSize: fs, fontWeight: 600, fill: 'var(--vn-text-primary)' }}
              >
                {n.icon ? `${n.icon} ` : ''}{n.label}
                <tspan style={{ fontSize: fsValue, fontWeight: 400, fill: 'var(--vn-text-muted)' }}>
                  {'  '}{valueText}
                </tspan>
              </text>
            ) : (
              <>
                <text
                  x={tx} y={n.labelY - 2} textAnchor={anchor}
                  style={{ fontFamily: 'var(--vn-font-body)', fontSize: fs, fontWeight: 600, fill: 'var(--vn-text-primary)' }}
                >
                  {n.icon ? `${n.icon} ` : ''}{n.label}
                </text>
                <text
                  x={tx} y={n.labelY + 12} textAnchor={anchor}
                  style={{ fontFamily: 'var(--vn-font-body)', fontSize: fsValue, fill: 'var(--vn-text-muted)' }}
                >
                  {valueText}
                </text>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}
